"use client";

import { useEffect, useRef, useState } from "react";
import { useChatStore } from "../../../../lib/chat-store/chats-store";
import ChatItem, { ChatItemLoading } from "./components/chat-item";
import ChatComponent from "./components/chat/chat";
import styles from "./page.module.css";
import { IoSearchOutline } from "react-icons/io5";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

export default function Chats() {
  const t = useTranslations("ChatsView");
  const isLoading = useChatStore((state) => state.isLoading);
  const chats = useChatStore((state) => state.chatsState.chats);
  const has_more_pages = useChatStore(
    (state) => state.chatsState.has_more_pages,
  );
  const last_cursor = useChatStore((state) => state.chatsState.last_cursor);
  const getChats = useChatStore((state) => state.getChats);
  const openChatId = useChatStore((state) => state.openChatId);
  const setOpenChatId = useChatStore((state) => state.setOpenChatId);
  const markAsRead = useChatStore((state) => state.markAsRead);
  const [search, setSearch] = useState("");
  const searchChatsFn = useChatStore((s) => s.searchChatsFn);
  const clearSearch = useChatStore((s) => s.clearSearch);
  const searchParams = useSearchParams();
  const chatIdFromUrl = searchParams.get("chatId");
  const isConnected = useChatStore((s) => s.isConnected);
  const requestedRef = useRef<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!chatIdFromUrl || !isConnected) return;
    if (requestedRef.current === chatIdFromUrl) return;
    const state = useChatStore.getState();
    const existing = state.chatsState.chats.find(
      (c) => c.conversation_id === chatIdFromUrl,
    );

    if (existing) {
      state.setOpenChatId(chatIdFromUrl);
      state.markAsRead(chatIdFromUrl);
      router.replace(pathname, { scroll: false });
      return;
    }

    requestedRef.current = chatIdFromUrl;

    useChatStore.getState().getChatById(chatIdFromUrl);
  }, [chatIdFromUrl, isConnected]);

  useEffect(() => {
    if (!chatIdFromUrl) return;
    if (openChatId !== chatIdFromUrl) return;

    router.replace(pathname, { scroll: false });
  }, [openChatId, chatIdFromUrl]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (search.length < 2) {
        clearSearch();
        return;
      }

      searchChatsFn(search);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, searchChatsFn, clearSearch]);

  const openChat = chats.find((c) => c.conversation_id === openChatId);

  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop - clientHeight < 20) {
      if (has_more_pages && !isFetchingMore && !isLoading) {
        console.log("Loading more chats...");
        setIsFetchingMore(true);
        getChats(last_cursor, "en");
      }
    }
  };

  // Reset fetching flag when chats update (request finished)
  useEffect(() => {
    setIsFetchingMore(false);
  }, [chats.length]);

  useEffect(() => {
    return () => {
      const state = useChatStore.getState();
      if (state.openChatId) {
        state.setOpenChatId("");
      }
    };
  }, []);

  return (
    <main className={styles.main}>
      <div className={styles.chatsWrapper}>
        <div
          className={`${styles.chatsListWrapper} ${openChat ? styles.hideOnMobile : ""}`}
        >
          <ul className={styles.chatsList} onScroll={handleListScroll}>
            <li className={styles.searchbar}>
              <span className={styles.icon}>
                <IoSearchOutline />
              </span>
              <input
                type="text"
                placeholder="Enter user name..."
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                }}
              />
            </li>
            {isLoading
              ? [...Array(12)].map((_, index) => (
                  <li key={index}>
                    <ChatItemLoading />
                  </li>
                ))
              : chats.map((chat) => (
                  <li key={chat.conversation_id}>
                    <ChatItem
                      chatData={chat}
                      isActiveChat={chat.conversation_id === openChatId}
                      onOpenChatAction={() => {
                        setOpenChatId(chat.conversation_id);
                        markAsRead(chat.conversation_id);
                      }}
                    />
                  </li>
                ))}
            {isFetchingMore && (
              <li>
                <ChatItemLoading />
              </li>
            )}
          </ul>
        </div>

        <div
          className={`${styles.chat} ${!openChat ? styles.hideOnMobile : ""}`}
        >
          {isLoading || !openChat ? (
            <h2 className={styles.hideOnMobile}>{t("journeyStartsHere")}</h2>
          ) : (
            <ChatComponent chatData={openChat} />
          )}
        </div>
      </div>
    </main>
  );
}
